import * as React from "react";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";

import { NotificationsOutlined } from "@/components/icons";
import { useMarkAllNotificationsRead, useUnreadNotificationCount } from "@/api/notifications";
import { usePageVisible } from "@/hooks/useBrowserPresence";

import { NotificationDrawer } from "./NotificationDrawer";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Rows about the session on screen are read as soon as they arrive.
 *
 *  The server notifies about a long turn, a waiting decision and so on
 *  without knowing whether the person is looking. The SPA does: while a
 *  session is open and the tab is visible, whenever the unread count
 *  moves (a pushed ``notification.created`` refetches it), that
 *  session's rows are marked read. So a user watching their turn finish
 *  sees no badge, and the e-mail they opted into is skipped too. */
function useReadWhileViewing(unread: number): void {
  const location = useLocation();
  const visible = usePageVisible();
  const { mutate } = useMarkAllNotificationsRead();
  const param = matchPath("/app/sessions/:sessionId", location.pathname)?.params.sessionId;
  // ``/app/sessions/archived`` shares the pattern; only an id is a session.
  const sessionId = param && UUID.test(param) ? param : null;
  React.useEffect(() => {
    if (!sessionId || !visible || unread === 0) return;
    mutate(sessionId);
  }, [sessionId, visible, unread, mutate]);
}

/** The AppShell's bell: the unread count, and the drawer behind it.
 *  Visible at every width (on a phone it takes the account avatar's
 *  slot; the account moves into the More menu). The bell toggles the
 *  drawer, and the drawer belongs to the page it was opened on: any
 *  navigation (a link elsewhere, the browser's Back) closes it, so it
 *  never lingers over the next page. */
export function NotificationBell(): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [openOn, setOpenOn] = React.useState<string | null>(null);
  // Leaving the page forgets the drawer, so coming back (Back, Forward,
  // a link) doesn't reopen it. Adjusted during render, React's pattern
  // for state derived from a changed prop, so it never paints open.
  if (openOn !== null && openOn !== pathname) setOpenOn(null);
  const open = openOn === pathname;
  const count = useUnreadNotificationCount();
  const unread = count.data?.count ?? 0;
  useReadWhileViewing(unread);
  const label =
    unread > 0 ? t("notifications.bell_unread", { count: unread }) : t("notifications.bell");
  return (
    <>
      <Tooltip title={label}>
        <IconButton
          color="inherit"
          onClick={() => setOpenOn(open ? null : pathname)}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <Badge badgeContent={unread} max={99} color="success">
            <NotificationsOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <NotificationDrawer open={open} onClose={() => setOpenOn(null)} />
    </>
  );
}
